import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './missionControl.css';
import SEOHead from '../components/UI/SEOHead';
import TypewriterText from '../components/UI/TypewriterText';
import { BlogCoverCard } from '../components/UI/BlogCoverCard';
import { waLink } from '../config';
import { FEATURED_POST, BLOG_POSTS, type CategorySlug } from '../data/blogPosts';
import { scrollToSection } from '../utils/scroll';
import heroVideoMp4 from '../assets/blog-hero-bg.mp4';
import heroPoster from '../assets/blog-hero-poster.jpg';

/* ──────────────────────────────────────────────────────────────────────────
   BLOG — rediseño "mission control" + hero en video (jul 2026). Estructura y
   lenguaje visual portados del mockup del usuario (Videos/Blog Hero
   Rediseño.dc.html): video de fondo con overlay + glows ambiente, titular con
   reveal por palabra + shimmer en el cierre, filtro de categorías con
   indicador deslizante, y tarjetas a foto completa con degradado inferior.
   Copy real (i18n), rutas reales (/blog/:slug) y datos reales de blogPosts.ts.

   Decisiones que SÍ se apartan del mockup (a propósito, no por descuido):
   1. El video es `position: fixed` detrás de TODA la página (pedido
      explícito del usuario tras ver la versión solo-en-hero, "para ver que
      tal optimizado") — se pausa con `visibilitychange` cuando la pestaña
      pierde foco, y solo se muestra en tema oscuro: en tema claro
      `[data-theme='light'] .blog-page-video` lo oculta por completo (un
      clip oscuro de fondo no combina con un layout claro, y así se evita
      cualquier costo de reproducción para quien usa el sitio en claro).
   2. Tipografía: Manrope (la del sitio), no Plus Jakarta Sans — introducir
      una segunda familia solo para esta sección rompería la consistencia
      con el resto del sitio.
   3. Sin nav flotante propia: el sitio ya tiene su Navbar real (idioma,
      tema, CTA) montado por App.tsx. La píldora "RurayHUB. Artículos
      Servicios Hablar" del mockup era el nav de un prototipo aislado, no
      algo pensado para convivir con la navegación real — duplicarla
      confundiría al usuario con dos barras de navegación.
   4. El video/glows solo se ven en tema oscuro; en tema claro la página
      vuelve a su fondo claro normal (grid, CTA) como ya lo hacía antes —
      no existe una versión "clara" del video, así que en vez de aclararlo
      artificialmente se oculta y el resto de la UI sigue el toggle de tema
      con normalidad.
   5. Categorías del filtro = los tags reales ya existentes en i18n
      (WhatsApp/CRM/IA/Métricas/Agenda), no la redacción del mockup
      ("WhatsApp AI", "CRM & Datos", "IA Clínicas") — mismo concepto, cero
      copy nuevo que traducir/mantener en 3 idiomas.
   ────────────────────────────────────────────────────────────────────────── */

const CATEGORY_ORDER: CategorySlug[] = ['whatsapp', 'crm', 'agenda', 'metricas', 'ia'];

// Representante de cada categoría — de dónde se saca la etiqueta ya traducida
// (post_N_tag) para el chip del filtro, sin inventar copy nuevo.
const CATEGORY_LABEL_SOURCE: Record<CategorySlug, string> = {
  whatsapp: 'post_1',
  crm: 'post_2',
  ia: 'post_3',
  metricas: 'post_4',
  agenda: 'featured',
};

type CategoryFilter = 'all' | CategorySlug;

const BlogPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToPost = (slug: string) => navigate(`/blog/${slug}`);
  const titleWords = t('blog_page.h1_scramble', 'Ideas para clínicas que').split(' ');

  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const filterTrackRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const chips = useMemo(
    () => [
      { id: 'all' as CategoryFilter, label: t('blog_page.category_all') },
      ...CATEGORY_ORDER.map((cat) => ({ id: cat as CategoryFilter, label: t(`blog_page.${CATEGORY_LABEL_SOURCE[cat]}_tag`) })),
    ],
    [t],
  );

  // Mide la posición del chip activo para deslizar la píldora blanca sobre él.
  // Se recalcula al cambiar de categoría, de idioma (cambia el ancho del
  // texto) y al redimensionar la ventana.
  useLayoutEffect(() => {
    const measure = () => {
      const track = filterTrackRef.current;
      const chip = chipRefs.current[activeCategory];
      if (!track || !chip) return;
      const trackRect = track.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();
      setIndicator({ left: chipRect.left - trackRect.left, width: chipRect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeCategory, chips]);

  // Un único carrusel horizontal con las tarjetas de la categoría activa (o
  // todas, con "Todos") — el chip filtra qué entra, y dentro se navega
  // arrastrando/con scroll (no hay una fila por categoría).
  // Todas las tarjetas (incluida la que antes era "destacado" fijo) entran
  // al mismo carrusel como iguales, todas mostradas en el formato grande
  // (variant="featured") — nada queda fuera ni en un tamaño distinto.
  const allPosts = useMemo(() => [FEATURED_POST, ...BLOG_POSTS], []);
  const carouselPosts = activeCategory === 'all' ? allPosts : allPosts.filter((p) => p.category === activeCategory);

  // Carrusel "coverflow" — tarjeta activa centrada y a tamaño completo,
  // vecinas asomando a los costados atenuadas/reducidas (referencia: fila de
  // tarjetas de equipo con la del medio resaltada). Scroll nativo con
  // scroll-snap (touch/trackpad/drag con mouse) + flechas/puntos, autoplay
  // que se pausa al interactuar — mismo espíritu que el carrusel de reseñas
  // del home, pero mostrando el "peek" de las tarjetas adyacentes en vez de
  // una sola tarjeta aislada.
  const carouselTotal = carouselPosts.length;
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselIndexRef = useRef(0);
  const carouselPausedRef = useRef(false);
  const carouselViewportRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimeout = useRef<number | undefined>(undefined);

  useEffect(() => { carouselIndexRef.current = carouselIndex; }, [carouselIndex]);

  const goToCarouselIndex = (i: number) => {
    if (carouselTotal === 0) return;
    setCarouselIndex(((i % carouselTotal) + carouselTotal) % carouselTotal);
  };
  const nextCarousel = () => goToCarouselIndex(carouselIndex + 1);
  const prevCarousel = () => goToCarouselIndex(carouselIndex - 1);

  // Desplaza el viewport para centrar la tarjeta activa cada vez que cambia
  // el índice (por flecha, punto, autoplay o clic en una tarjeta lateral).
  // Se hace con scrollTo sobre el propio viewport horizontal (no
  // scrollIntoView): scrollIntoView con block:'nearest' igual arrastra el
  // scroll VERTICAL de toda la página cuando la sección está fuera de
  // vista (p.ej. justo al entrar a /blog, antes de que el usuario baje) —
  // pisaba el scroll-to-top global de App.tsx. Calculando el scrollLeft a
  // mano evitamos tocar el eje vertical por completo.
  useEffect(() => {
    const viewport = carouselViewportRef.current;
    const el = slideRefs.current[carouselIndex];
    if (!viewport || !el) return;
    isProgrammaticScroll.current = true;
    const target = el.offsetLeft - (viewport.clientWidth - el.clientWidth) / 2;
    viewport.scrollTo({ left: target, behavior: 'smooth' });
    window.clearTimeout(programmaticScrollTimeout.current);
    programmaticScrollTimeout.current = window.setTimeout(() => { isProgrammaticScroll.current = false; }, 500);
  }, [carouselIndex, carouselPosts]);

  useEffect(() => {
    if (carouselTotal < 2) return;
    const id = setInterval(() => {
      if (carouselPausedRef.current) return;
      setCarouselIndex((i) => (i + 1) % carouselTotal);
    }, 6000);
    return () => clearInterval(id);
  }, [carouselTotal]);

  // Al cambiar de categoría el set de tarjetas cambia por completo — vuelve
  // al índice 0 en vez de quedarse en una posición que ya no corresponde.
  // Ajustado durante el render (no en un efecto) siguiendo el patrón de
  // React para resetear estado derivado de un prop/estado que cambió,
  // evita el cascading render que dispara la regla set-state-in-effect.
  const [prevCategory, setPrevCategory] = useState(activeCategory);
  if (prevCategory !== activeCategory) {
    setPrevCategory(activeCategory);
    setCarouselIndex(0);
  }

  // Escala/atenúa cada tarjeta según su distancia al centro del viewport —
  // esto es lo que produce el efecto "coverflow" (centro nítido, costados
  // chicos y difuminados) tanto durante el scroll manual como el autoplay.
  // Se aplica directo al DOM (no a estado de React) para que siga el gesto
  // de swipe cuadro a cuadro sin re-renders.
  useEffect(() => {
    const viewport = carouselViewportRef.current;
    if (!viewport) return;
    let raf = 0;
    const applyStyles = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const centerX = viewportRect.left + viewportRect.width / 2;
      let closestIdx = carouselIndexRef.current;
      let closestDist = Infinity;
      slideRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const dist = Math.abs(cardCenter - centerX);
        const norm = Math.min(dist / (viewportRect.width / 2 || 1), 1.4);
        el.style.transform = `scale(${1 - norm * 0.18})`;
        el.style.opacity = String(Math.max(1 - norm * 0.8, 0.32));
        el.style.filter = norm > 0.12 ? `saturate(${Math.max(1 - norm * 0.65, 0.2)})` : 'none';
        if (dist < closestDist) { closestDist = dist; closestIdx = i; }
      });
      if (!isProgrammaticScroll.current && closestIdx !== carouselIndexRef.current) {
        setCarouselIndex(closestIdx);
      }
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyStyles);
    };
    applyStyles();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      viewport.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [carouselPosts]);

  // Arrastre con mouse en desktop (touch/trackpad ya funcionan nativo vía
  // overflow-x + scroll-snap). Suprime el click de navegación de la tarjeta
  // si el gesto se movió más que un tap, para no abrir un post sin querer.
  useEffect(() => {
    const viewport = carouselViewportRef.current;
    if (!viewport) return;
    let isDown = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    const suppressClick = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    const onPointerDown = (e: PointerEvent) => {
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 6) moved = true;
      viewport.scrollLeft = startScroll - dx;
    };
    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      viewport.classList.remove('is-dragging');
      if (moved) viewport.addEventListener('click', suppressClick, { capture: true, once: true });
    };
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', endDrag);
      viewport.removeEventListener('pointerleave', endDrag);
    };
  }, []);

  // El video corre de fondo mientras dure la visita a /blog (fixed, toda la
  // página) — se pausa cuando la pestaña pierde foco para no gastar batería
  // de más en algo que ni siquiera se está viendo.
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const onVisibility = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else v.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // El clip dura solo ~5s y su último frame no encaja con el primero, así
  // que el `loop` nativo del <video> se ve como un corte feo. En vez de
  // re-exportar el asset, se disimula con un fundido a negro (el fondo/wash
  // ya es oscuro, así que el dip se siente como una transición, no un parche):
  // controlamos el loop a mano — al acercarse al final bajamos la opacidad,
  // al terminar reiniciamos el frame y la subimos de nuevo.
  // 04-ago-2026: bajado de 550ms a 200ms — con el video ahora fijo en TODA
  // la página (antes solo en el hero, se veía brevemente al pasar de largo),
  // ese fundido se repite frente a los ojos de quien está leyendo cada ~5s;
  // acortarlo lo vuelve un parpadeo casi imperceptible en vez de una pausa
  // notoria. Ver también preload="auto" más abajo — con metadata-only el
  // reinicio del loop podía sumar una espera de red encima del fundido.
  const VIDEO_FADE_MS = 200;
  const [videoFading, setVideoFading] = useState(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let fading = false;

    const onTimeUpdate = () => {
      if (!fading && v.duration && v.currentTime >= v.duration - VIDEO_FADE_MS / 1000) {
        fading = true;
        setVideoFading(true);
      }
    };
    const onEnded = () => {
      v.currentTime = 0;
      v.play().catch(() => {});
      fading = false;
      setVideoFading(false);
    };

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('ended', onEnded);
    return () => {
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('ended', onEnded);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="mc-page"
      style={{ position: 'relative' }}
    >
      <SEOHead
        title="Blog"
        description="Artículos sobre automatización de WhatsApp, CRM para clínicas dentales, inteligencia artificial y estrategias para aumentar pacientes."
        path="/blog"
      />

      <div style={{ position: 'relative', zIndex: 1 }}>

      {/* ── Video de fondo fijo para TODA la página (no solo el hero) — pedido
          explícito del usuario (04-ago-2026): antes estaba así (ver historial),
          un cambio posterior lo dejó confinado al hero sin documentarlo, se
          restaura acá. `position: fixed` + z-index bajo, por detrás de todo
          el contenido (que ya vive en su propio wrapper z-index:1 más abajo).
          Se pausa con visibilitychange cuando la pestaña pierde foco (ver
          arriba) y solo se muestra en tema oscuro — un clip oscuro fijo no
          combina con un layout claro. ── */}
      <div aria-hidden className="blog-page-video-wrap">
        <video
          ref={videoRef}
          className="blog-hero-video"
          autoPlay
          muted
          playsInline
          preload="auto"
          poster={heroPoster}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
            opacity: videoFading ? 0 : 1,
            transition: `opacity ${VIDEO_FADE_MS}ms ease`,
          }}
        >
          <source src={heroVideoMp4} type="video/mp4" />
        </video>
        {/* Difuminado de bordes sup/inf — el clip trae una marca de agua de
            Gemini fija en la esquina inferior derecha; en vez de recortar el
            video (perdería encuadre), se funde el borde superior e inferior
            hacia el mismo negro sólido del fondo, disimulando la marca sin
            cortes visibles duros. */}
        <div aria-hidden className="blog-hero-wm-mask" />
        {/* Wash general — ya no solo del hero, cubre toda la altura fija del
            video para que el texto de cualquier sección (no solo el hero)
            siga siendo legible sobre el clip en movimiento. */}
        <div aria-hidden className="blog-page-wash" />
      </div>

      {/* ── HERO — glows ambiente propios de esta sección (el video ya no
          vive acá, ver arriba). ── */}
      <div className="blog-hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* wash-a es más fuerte que el wash general de página (blog-page-wash)
            — el título necesita más contraste que el resto del contenido, así
            que se apila encima solo en esta sección. */}
        <div aria-hidden className="blog-hero-wash-a" />
        <div aria-hidden className="blog-hero-wash-b" />
        <div aria-hidden className="blog-glow blog-glow-a" />
        <div aria-hidden className="blog-glow blog-glow-b" />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1120, margin: '0 auto', padding: 'clamp(5.5rem, 13vh, 8rem) 1.5rem clamp(4rem, 9vh, 6rem)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mc-mono"
            style={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-primary)', opacity: 0.6 }}
          >
            <TypewriterText text={t('blog_page.eyebrow')} />
          </motion.span>

          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2.4rem, 5.5vw, 4rem)', fontWeight: 800, lineHeight: 1.12, letterSpacing: '-0.03em', color: 'var(--text-primary)', margin: '1rem 0 1.3rem', maxWidth: '20ch' }}>
            {titleWords.map((word, i) => (
              <span key={i} style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'top' }}>
                <motion.span
                  initial={{ y: '110%' }}
                  animate={{ y: '0%' }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  style={{ display: 'inline-block' }}
                >
                  {word}&nbsp;
                </motion.span>
              </span>
            ))}
            <span style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'top' }}>
              <motion.span
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 0.7, delay: 0.15 + titleWords.length * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="blog-hero-shimmer"
                style={{ display: 'inline-block' }}
              >
                {t('blog_page.h1_grad', 'no quieren perder pacientes')}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 + (titleWords.length + 1) * 0.07 + 0.1 }}
            style={{ fontSize: 'clamp(1rem, 1.4vw, 1.15rem)', lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 0 2rem', fontWeight: 500 }}
          >
            {t('blog_page.lead')}
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 + (titleWords.length + 1) * 0.07 + 0.22 }}
            whileHover={{ y: -3, boxShadow: '0 16px 32px rgba(0,0,0,.15)' }}
            onClick={() => scrollToSection('#articulos')}
            className="trial-btn"
            style={{ display: 'inline-flex', fontSize: '.95rem', fontWeight: 700, padding: '.9rem 1.9rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: 'var(--font-heading)' }}
          >
            {t('blog_page.hero_cta')}
          </motion.button>
        </div>
      </div>

      {/* ── NAV DE CATEGORÍAS (salta a la fila, ya no filtra) + DESTACADO ── */}
      <section id="articulos" style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(3.5rem, 7vw, 5rem) 1.5rem 1rem' }}>
        <div ref={filterTrackRef} style={{ position: 'relative', display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '2.75rem' }}>
          <AnimatePresence>
            {indicator && (
              <motion.div
                aria-hidden
                initial={false}
                animate={{ left: indicator.left, width: indicator.width }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: 'absolute', top: 0, bottom: 0, background: '#fff', borderRadius: 999, zIndex: 0 }}
              />
            )}
          </AnimatePresence>
          {chips.map((chip) => {
            const isActive = activeCategory === chip.id;
            return (
              <button
                key={chip.id}
                ref={(el) => { chipRefs.current[chip.id] = el; }}
                onClick={() => setActiveCategory(chip.id)}
                className="blog-filter-chip"
                style={{
                  position: 'relative', zIndex: 1,
                  background: 'transparent',
                  color: isActive ? '#000' : 'var(--text-secondary)',
                  border: `1px solid ${isActive ? 'transparent' : 'var(--border-lighter)'}`,
                  borderRadius: 999, padding: '.55rem 1.1rem', fontSize: '.85rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                  transition: 'color .35s cubic-bezier(.16,1,.3,1)',
                }}
              >
                {chip.label}
              </button>
            );
          })}
        </div>

        {/* Carrusel "coverflow" — tarjeta activa centrada a tamaño completo,
            vecinas asomando a los costados, chicas y atenuadas. Scroll
            nativo con snap (drag, touch, trackpad) + flechas/puntos. Se
            escapa del max-width:1120 del <section> a todo el ancho del
            viewport para que las tarjetas laterales tengan espacio real
            donde asomar (overflow:hidden evita cualquier scroll horizontal
            de página por el redondeo de 100vw vs. el ancho real). */}
        {carouselPosts.length > 0 && (
          <div style={{ position: 'relative', width: '100vw', marginLeft: 'calc(50% - 50vw)', overflow: 'hidden' }}>
            <div
              ref={carouselViewportRef}
              className="blog-coverflow-viewport"
              onMouseEnter={() => { carouselPausedRef.current = true; }}
              onMouseLeave={() => { carouselPausedRef.current = false; }}
            >
              <div className="blog-coverflow-track">
                {carouselPosts.map((post, i) => (
                  <div
                    key={post.key}
                    ref={(el) => { slideRefs.current[i] = el; }}
                    className={`blog-coverflow-slide${i === carouselIndex ? ' is-active' : ''}`}
                  >
                    <BlogCoverCard
                      post={post}
                      variant="featured"
                      onClick={() => (i === carouselIndex ? goToPost(post.slug) : goToCarouselIndex(i))}
                    />
                  </div>
                ))}
              </div>
            </div>

            {carouselTotal > 1 && (
              <>
                <button aria-label={t('blog_page.carousel_prev', 'Anterior')} onClick={prevCarousel} className="blog-carousel-arrow blog-carousel-arrow-left">
                  <ChevronLeft size={20} />
                </button>
                <button aria-label={t('blog_page.carousel_next', 'Siguiente')} onClick={nextCarousel} className="blog-carousel-arrow blog-carousel-arrow-right">
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
        )}

        {carouselTotal > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: '1.5rem' }}>
            {carouselPosts.map((_, i) => (
              <button
                key={i}
                aria-label={t('blog_page.carousel_dot', 'Ir a la tarjeta {{n}}', { n: i + 1 })}
                aria-current={i === carouselIndex}
                onClick={() => goToCarouselIndex(i)}
                className="blog-carousel-dot"
                style={{ width: i === carouselIndex ? 22 : 8, background: i === carouselIndex ? 'var(--accent-teal)' : undefined }}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(4.5rem, 9vh, 7rem) 0 clamp(5rem, 10vh, 8rem)', position: 'relative' }}>
        <div className="container">
          <motion.div
            className="prod-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative', overflow: 'hidden', borderRadius: 32,
              padding: 'clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem)',
              textAlign: 'center',
              backdropFilter: 'blur(16px)',
            }}
          >
            <div aria-hidden style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--accent-teal), transparent)', opacity: 0.55, pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: '#00e5d1', opacity: 0.16, filter: 'blur(100px)', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', bottom: -120, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'var(--accent-purple)', opacity: 0.08, filter: 'blur(110px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto' }}>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1.6rem', lineHeight: 1.12, letterSpacing: '-0.03em' }}
              >
                {t('blog_page.cta_title')}
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.18, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', gap: '.9rem', justifyContent: 'center', flexWrap: 'wrap' }}
              >
                <a href={waLink(t('blog_page.wa_message'))} target="_blank" rel="noopener noreferrer" className="trial-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
                  {t('blog_page.cta_primary')}
                </a>
                <a href="/servicios" className="ghost-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
                  {t('blog_page.cta_secondary')}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      </div>

      <style>{`
        /* Wrapper del video a nivel de página — position:fixed (no absolute)
           para que cubra el viewport completo sin importar cuánto se scrollee,
           en vez de quedar confinado a la altura de un contenedor. El fondo
           #070708 es el fallback detrás del <video> (se asoma ~550ms durante
           el fundido del loop, ver VIDEO_FADE_MS) — tiene que matchear el
           tono de los washes para que ese instante no parpadee como una
           franja de color distinto. Oculto en tema claro: un clip oscuro fijo
           no combina con un layout claro, y así se evita el costo de
           reproducirlo para quien usa el sitio en claro. */
        .blog-page-video-wrap { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; background: #070708; }
        [data-theme='light'] .blog-page-video-wrap { display: none; }
        /* El hero ya NO tiene fondo sólido propio — antes bloqueaba el video
           fijo de abajo justo en la sección donde más se necesita ver. El
           contraste del título ahora lo dan wash-a/wash-b apiladas encima. */
        .blog-hero { background: transparent; }
        [data-theme='light'] .blog-hero { background: #f5f5f5; }
        /* opacity, no transform — es lo único que de verdad se anima (el
           fundido del loop, ver VIDEO_FADE_MS). will-change:transform acá
           forzaba una capa de composición para una transformación que nunca
           ocurre, sin ayudar al repintado real. */
        .blog-hero-video { will-change: opacity; }
        /* En claro el video (clip oscuro) se veía demasiado pesado — mismo
           criterio que ya usa el hero de "Por qué nosotros" (video/foto
           tenue que se funde con el fondo claro): acá se invierte el color
           del clip (oscuros → claros) y se corrige el matiz con hue-rotate
           para que no quede en negativo raro, solo más liviano. Los washes
           oscuros de abajo se quedan igual — sirven para que el título en
           blanco siga siendo legible sobre el video ya aclarado. */
        [data-theme='light'] .blog-hero-video { filter: invert(1) hue-rotate(180deg); }

        /* Las 3 capas de "wash" sobre el video (máscara de la marca de agua +
           2 degradados de contraste para el título) estaban fijas en negro
           puro (#000 / rgba(7,7,8,x)) — con el video ya invertido a tonos
           claros en tema claro, esas franjas negras quedaban como bandas
           duras arriba y abajo en vez de disimularse. En claro pasan a un
           tono claro (mismo valor que --bg-dark ahí, el fondo real de la
           página) para que sigan tapando la marca de agua y dando contraste
           al título, pero fundiéndose con el video aclarado y con la página
           de abajo — no como bloques negros pegados encima. */
        .blog-hero-wm-mask { position: absolute; inset: 0; background: linear-gradient(180deg, #000 0%, transparent 14%, transparent 52%, #000 90%); }
        [data-theme='light'] .blog-hero-wm-mask { background: linear-gradient(180deg, #f5f5f5 0%, transparent 14%, transparent 52%, #f5f5f5 90%); }
        .blog-hero-wash-a { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(7,7,8,.58) 0%, rgba(7,7,8,.58) 45%, transparent 80%); }
        [data-theme='light'] .blog-hero-wash-a { background: linear-gradient(180deg, rgba(245,245,247,.7) 0%, rgba(245,245,247,.7) 45%, transparent 80%); }
        .blog-hero-wash-b { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(7,7,8,.05) 0%, rgba(7,7,8,.35) 45%, transparent 75%); }
        [data-theme='light'] .blog-hero-wash-b { background: linear-gradient(180deg, rgba(245,245,247,.05) 0%, rgba(245,245,247,.35) 45%, transparent 75%); }

        .blog-glow { position: absolute; border-radius: 50%; filter: blur(60px); pointer-events: none; z-index: 1; }
        .blog-glow-a { top: -10%; left: -5%; width: 600px; height: 600px; background: radial-gradient(circle, rgba(0,229,209,.16) 0%, transparent 60%); animation: blog-drift-a 12s ease-in-out infinite alternate; }
        .blog-glow-b { top: 0; right: -10%; width: 460px; height: 460px; background: radial-gradient(circle, rgba(139,92,246,.13) 0%, transparent 60%); animation: blog-drift-b 14s ease-in-out infinite alternate; }
        @keyframes blog-drift-a { from { transform: translate(0,0); } to { transform: translate(30px,40px); } }
        @keyframes blog-drift-b { from { transform: translate(0,0); } to { transform: translate(-25px,-30px); } }

        .blog-hero-shimmer {
          font-weight: 800;
          background: linear-gradient(110deg, #f5f5f5 0%, #c9d6d4 22%, #00e5d1 45%, #eafffb 55%, #c9d6d4 72%, #f5f5f5 100%);
          background-size: 220% 100%;
          animation: blog-shimmer 5s ease-in-out infinite;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        /* En claro, el truco de degradado + background-clip:text se veía
           como un bloque sólido en vez de letras (algo en el stacking del
           hero ya aclarado lo rompía). En vez de perseguir esa causa, en
           claro se resuelve más simple y a prueba de fallos: texto sólido
           color acento, sin degradado ni clip — se sigue leyendo el énfasis
           en la frase sin arriesgar el mismo bug. */
        [data-theme='light'] .blog-hero-shimmer {
          background: none;
          -webkit-text-fill-color: initial;
          color: #00a896;
          animation: none;
        }
        @keyframes blog-shimmer { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }

        .blog-filter-chip:hover { border-color: var(--accent-teal) !important; }

        /* Carrusel coverflow — viewport a todo el ancho (100vw, ver JSX),
           track centrado vía scroll-snap con padding lateral = la mitad del
           "sobrante" (ancho del viewport menos el de la tarjeta), lo que
           deja a las tarjetas de las puntas asomar y quedar centrables por
           igual que las del medio. --bcf-card-w controla cuánto se ve de la
           tarjeta activa; el resto (más chico y atenuado) lo resuelve el
           listener de scroll en JS aplicando scale/opacity por distancia al
           centro — así el efecto seguía el gesto cuadro a cuadro, no solo al
           soltar. */
        .blog-coverflow-viewport {
          --bcf-card-w: clamp(280px, 58vw, 620px);
          position: relative;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          cursor: grab;
        }
        .blog-coverflow-viewport::-webkit-scrollbar { display: none; }
        .blog-coverflow-viewport.is-dragging { cursor: grabbing; scroll-snap-type: none; }
        .blog-coverflow-track {
          display: flex;
          align-items: center;
          gap: clamp(14px, 2.4vw, 28px);
          padding: 1.5rem calc((100% - var(--bcf-card-w)) / 2);
          width: max-content;
        }
        .blog-coverflow-slide {
          flex: 0 0 var(--bcf-card-w);
          scroll-snap-align: center;
          opacity: .45;
          transform: scale(.86);
          transition: opacity .15s linear;
          will-change: transform, opacity, filter;
        }
        .blog-coverflow-slide .blog-cover-card { border: 1px solid transparent; transition: border-color .35s ease, box-shadow .35s ease; }
        .blog-coverflow-slide.is-active { opacity: 1; transform: scale(1); }
        .blog-coverflow-slide.is-active .blog-cover-card { border-color: rgba(255,255,255,.16); box-shadow: 0 28px 64px -22px rgba(0,0,0,.65); }
        [data-theme='light'] .blog-coverflow-slide.is-active .blog-cover-card { border-color: rgba(0,0,0,.1); box-shadow: 0 28px 64px -26px rgba(0,0,0,.3); }

        .blog-carousel-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 40px; height: 40px; border-radius: 50%; display: grid; place-items: center;
          appearance: none; -webkit-appearance: none; border: none; padding: 0;
          background: rgba(255,255,255,.16); backdrop-filter: blur(6px);
          color: #fff; cursor: pointer; z-index: 2;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .blog-carousel-arrow:hover { background: #fff; color: #000; transform: translateY(-50%) scale(1.08); }
        .blog-carousel-arrow-left { left: clamp(8px, 2vw, 32px); }
        .blog-carousel-arrow-right { right: clamp(8px, 2vw, 32px); }

        .blog-carousel-dot {
          height: 8px; border-radius: 999px; border: none; padding: 0;
          background: var(--border-lighter); cursor: pointer;
          transition: width 0.3s ease, background 0.3s ease;
        }

        .trial-btn {
          background: #fff;
          color: #000;
          border: none;
          transition: background 0.2s ease;
        }
        .trial-btn:hover { background: #e8e8ed; }
        [data-theme='light'] .trial-btn { background: #000; color: #fff; }
        [data-theme='light'] .trial-btn:hover { background: #1d1d1f; }

        .ghost-btn {
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.04); color: var(--text-primary);
          border: 1px solid var(--border-lighter);
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .ghost-btn:hover { background: rgba(255,255,255,0.07); border-color: var(--accent-teal); }
        [data-theme='light'] .ghost-btn { background: rgba(0,0,0,0.03); }
        [data-theme='light'] .ghost-btn:hover { background: rgba(0,0,0,0.06); }

        .prod-cta {
          /* El borde de la tarjeta terminaba en rgba(8,8,8,.6) — semi-
             transparente, no negro puro — dejando un contorno unas unidades
             más claro que el negro real de la página detrás; se ve como una
             división marcada aunque la diferencia numérica sea chica. Ahora
             cierra en negro 100% opaco, igual al fondo real. */
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, #000 100%);
        }
        [data-theme='light'] .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }

        @media (prefers-reduced-motion: reduce) {
          .blog-glow-a, .blog-glow-b, .blog-hero-shimmer { animation: none; }
        }
      `}</style>
    </motion.div>
  );
};

export default BlogPage;
